import React, { useEffect, useState } from 'react';
import { format, fromUnixTime } from 'date-fns';

import { store } from 'redux/store';
import { setCurrentTab } from 'redux/reducers/meteorologyFieldsLayoutReducer';
import Instructions from 'components/Meteorology/Fields/NumericForecast/Instructions';
import Toolbar from 'components/Meteorology/Fields/NumericForecast/Toolbar';
import Display from 'components/Meteorology/Fields/Display';
import ZoomButtons from 'components/Meteorology/Fields/ZoomButtons';
import NumericForecastHeatmapDisplay from 'components/Meteorology/Fields/NumericForecastHeatmap';
import TitleBar from 'components/Meteorology/Fields/NumericForecast/TitleBar';
import { ResponseError } from 'services/api/base';
import { useSelector } from 'react-redux';
import {
  selectMFControlPanelRegion,
  selectMFControlPanelModels,
  selectMFControlPanelForecastUnixDate,
  selectMFControlPanelPeriod,
  selectMFControlPanelHeatmapImages,
  selectMFControlPanelConsultRequestId,
  selectMFControlPanelLastConsulted,
} from 'redux/reducers/meteorologyFieldsControlPanelReducer';
import buildImageDisplayData from 'helper/products/numericForecast/buildImageDisplayData';
import useImagesDownload from 'utils/hooks/meteorology/numericForecast/useImagesDownload';
import { useHandleRequestErrors } from 'utils/hooks';
import useImages from 'utils/hooks/meteorology/numericForecast/useImages';
import useImageRowSize from 'utils/hooks/meteorology/useImageRowSize';
import useIsMobile from 'utils/hooks/useIsMobile';
import NumericForecastMobile from 'components/Meteorology/Fields/NumericForecast/Mobile';

export default function NumericForecast() {
  const isMobile = useIsMobile();
  const [multiMapMode, setMultiMapMode] = useState(false);
  const {
    rowSize,
    maxRowSize,
  } = useImageRowSize();

  const forecastUnixDate = useSelector(selectMFControlPanelForecastUnixDate);
  const models = useSelector(selectMFControlPanelModels);
  const region = useSelector(selectMFControlPanelRegion);
  const period = useSelector(selectMFControlPanelPeriod);
  const heatmapImages = useSelector(selectMFControlPanelHeatmapImages);
  const consultRequestId = useSelector(selectMFControlPanelConsultRequestId);
  const lastConsulted = useSelector(selectMFControlPanelLastConsulted);
  const isConsultedHeatmap = lastConsulted?.viewMode === 'heatmap';

  const showInstructions = consultRequestId === 0
    && !forecastUnixDate && !region && !period && models.length === 0;

  const {
    images, isFetching, error, areAllImagesFetched,
  } = useImages(multiMapMode);

  const {
    imageSets,
    columnLabels,
    rowLabels,
  } = buildImageDisplayData(images, rowSize, multiMapMode);
  const {
    onDownloadClick,
    downloadDisabled,
  } = useImagesDownload(images, areAllImagesFetched);

  useHandleRequestErrors(error as ResponseError);

  useEffect(() => {
    store.dispatch(setCurrentTab('numericforecast'));
  }, []);

  if (isMobile) {
    return <NumericForecastMobile />;
  }

  return (
    <div>
      {!isConsultedHeatmap && (
        <Toolbar
          showInstructions={showInstructions}
          multiMapMode={multiMapMode}
          setMultiMapMode={setMultiMapMode}
        />
      )}
      <TitleBar
        showDownload={!isConsultedHeatmap}
        showInstructions={showInstructions}
        onDownloadClick={onDownloadClick}
        downloadDisabled={downloadDisabled}
      />
      {showInstructions && <Instructions />}
      {!showInstructions && isConsultedHeatmap && (
        <NumericForecastHeatmapDisplay
          images={heatmapImages}
          fetching={isFetching}
          forecastDate={
            lastConsulted?.forecastUnixDate
              ? format(fromUnixTime(lastConsulted.forecastUnixDate), 'dd/MM/yyyy')
              : undefined
          }
        />
      )}
      {!showInstructions && !isConsultedHeatmap && (
        <>
          <Display
            imageSets={imageSets}
            columnLabels={columnLabels}
            rowLabels={rowLabels}
            fetchingImages={isFetching}
            fullSweep={!multiMapMode}
          />
          {maxRowSize > 1 && (
            <ZoomButtons
              imagesQuantity={maxRowSize}
            />
          )}
        </>
      )}
    </div>
  );
}
